import * as THREE from 'three';
import { IMPACT_CAM, SIM } from '../config/tuning';
import type { FlightState } from '../flight/FlightState';

/**
 * The impact replay (SPEC §3.3.1, §3.8.2, M7).
 *
 * This is the **single exception** to design pillar 4 — the game is otherwise strictly what the
 * camera on the nose can see. SPEC §3.3.1 permits it precisely because it happens *after the
 * outcome is decided*, so it grants no advantage: the run is over, and what is left is to show
 * the player what they did.
 *
 * It replays the last 2.5 seconds of the terminal dive from outside the aircraft, which is where
 * the whole run finally becomes legible — the dive angle they held, the aim point they were
 * tracking, and whether the thing they were aiming at is now on fire.
 *
 * The recorder is a plain ring buffer of transforms at the simulation rate. That is enough for
 * this, and it is the same shape the full replay recorder at M10 will need.
 */

interface Frame {
  readonly positionM: THREE.Vector3;
  readonly orientation: THREE.Quaternion;
  speedMs: number;
}

const cameraOffset = new THREE.Vector3();
const lookTarget = new THREE.Vector3();
const smoothedPosition = new THREE.Vector3();

export class ImpactCam {
  /** Frames retained, at the simulation rate. */
  private readonly frames: Frame[] = [];
  private writeIndex = 0;
  private recorded = 0;

  private playing = false;
  private playbackS = 0;
  /** World position the camera orbits — where the run ended. */
  private readonly impactM = new THREE.Vector3();
  private orbitPhase = 0;

  constructor() {
    const capacity = Math.ceil(IMPACT_CAM.durationS / SIM.FIXED_DT);
    for (let i = 0; i < capacity; i += 1) {
      this.frames.push({
        positionM: new THREE.Vector3(),
        orientation: new THREE.Quaternion(),
        speedMs: 0,
      });
    }
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  /** Seconds into the replay, for the HUD suppression and the debrief hand-off. */
  get elapsedS(): number {
    return this.playbackS;
  }

  /** Record one tick. Cheap enough to run unconditionally — it is three copies. */
  record(state: FlightState): void {
    if (this.playing) return;
    const frame = this.frames[this.writeIndex];
    frame.positionM.copy(state.position);
    frame.orientation.copy(state.orientation);
    frame.speedMs = state.velocity.length();

    this.writeIndex = (this.writeIndex + 1) % this.frames.length;
    if (this.recorded < this.frames.length) this.recorded += 1;
  }

  /** Begin the replay at an impact point. */
  start(impactM: THREE.Vector3): void {
    if (this.playing) return;
    this.impactM.copy(impactM);
    this.playing = true;
    this.playbackS = 0;
    this.orbitPhase = 0;
  }

  stop(): void {
    this.playing = false;
    this.playbackS = 0;
  }

  reset(): void {
    this.stop();
    this.recorded = 0;
    this.writeIndex = 0;
  }

  /**
   * Advance the replay and place `camera`. Returns false once it has finished.
   *
   * Runs on the render clock, not the simulation: the world is frozen behind it, and the replay
   * is presentation.
   */
  update(dtS: number, camera: THREE.PerspectiveCamera): boolean {
    if (!this.playing) return false;

    this.playbackS += dtS * IMPACT_CAM.timeScale;
    if (this.playbackS >= IMPACT_CAM.durationS) {
      this.playing = false;
      return false;
    }

    const frame = this.frameAt(this.playbackS);
    if (frame === null) {
      this.playing = false;
      return false;
    }

    // A slow orbit around the impact, framed so the aircraft comes *toward* the camera — which is
    // the shot that makes a dive angle readable, and the framing the reference footage uses.
    this.orbitPhase += dtS * IMPACT_CAM.orbitRateRadS;
    const radius = IMPACT_CAM.radiusM;
    cameraOffset.set(
      Math.cos(this.orbitPhase) * radius,
      IMPACT_CAM.heightM,
      Math.sin(this.orbitPhase) * radius,
    );

    smoothedPosition.copy(this.impactM).add(cameraOffset);
    camera.position.copy(smoothedPosition);

    /**
     * Hold on the impact point and let the aircraft fly into frame.
     *
     * Tracking the aircraft instead is the obvious idea and it is wrong: at the start of the
     * replay the aircraft is hundreds of metres up the dive line and high, so the camera pitches
     * up into empty sky and the target — the whole subject of the shot — falls out of frame
     * entirely. Worse, the auto-exposure then meters for blank haze, so when the warhead does go
     * off it blows the screen to white for a second and the payoff is unreadable.
     *
     * Holding the aim point keeps the target lit and framed throughout, and the aircraft enters
     * shot on its own as it arrives. That is how the shot is composed in the reference footage,
     * and it is the only version in which the dive angle is legible against the ground.
     */
    lookTarget.copy(this.impactM);
    camera.lookAt(lookTarget);
    camera.updateMatrixWorld(true);
    return true;
  }

  /** The recorded aircraft transform at a point in the replay, for drawing the airframe. */
  frameAt(playbackS: number): Frame | null {
    if (this.recorded === 0) return null;
    const tickIndex = Math.floor(playbackS / SIM.FIXED_DT);
    // The buffer holds the *last* `recorded` frames; playback starts at the oldest of them.
    const oldest = (this.writeIndex - this.recorded + this.frames.length) % this.frames.length;
    const index = (oldest + Math.min(tickIndex, this.recorded - 1)) % this.frames.length;
    return this.frames[index];
  }
}
